import type { Coord, OffsetCoord } from "./types";

export const HEX_SIZE = 45;
const ROOT_THREE = Math.sqrt(3);

export const AXIAL_DIRECTIONS: readonly Coord[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
];

export const TRANSECT_DIRECTIONS: readonly (
  readonly [negative: Coord, positive: Coord]
)[] = [
  [AXIAL_DIRECTIONS[3] as Coord, AXIAL_DIRECTIONS[0] as Coord],
  [AXIAL_DIRECTIONS[4] as Coord, AXIAL_DIRECTIONS[1] as Coord],
  [AXIAL_DIRECTIONS[5] as Coord, AXIAL_DIRECTIONS[2] as Coord],
];

export function offsetToAxial([column, row]: OffsetCoord): Coord {
  return {
    q: column - Math.floor(row / 2),
    r: row,
  };
}

export function coordKey(coord: Coord): string {
  return `${coord.q},${coord.r}`;
}

export function offsetKey(offset: OffsetCoord): string {
  return coordKey(offsetToAxial(offset));
}

export function addCoords(left: Coord, right: Coord): Coord {
  return {
    q: left.q + right.q,
    r: left.r + right.r,
  };
}

export function scaleCoord(coord: Coord, factor: number): Coord {
  return {
    q: coord.q * factor,
    r: coord.r * factor,
  };
}

export function areNeighbors(left: Coord, right: Coord): boolean {
  return AXIAL_DIRECTIONS.some(
    (direction) =>
      left.q + direction.q === right.q && left.r + direction.r === right.r,
  );
}

export function edgeKey(left: string, right: string): string {
  return left < right ? `${left}|${right}` : `${right}|${left}`;
}

export function directedEdgeKey(from: string, to: string): string {
  return `${from}>${to}`;
}

export function splitEdgeKey(key: string): readonly [string, string] {
  const separator = key.indexOf("|");
  if (separator === -1) {
    throw new Error(`Invalid edge key: ${key}`);
  }
  return [key.slice(0, separator), key.slice(separator + 1)];
}

export function hexCenter(coord: Coord): Readonly<{ x: number; y: number }> {
  return {
    x: HEX_SIZE * ROOT_THREE * (coord.q + coord.r / 2),
    y: HEX_SIZE * 1.5 * coord.r,
  };
}

export function hexPoints(coord: Coord): string {
  const center = hexCenter(coord);
  const points: string[] = [];

  for (let index = 0; index < 6; index += 1) {
    const angle = ((60 * index - 30) * Math.PI) / 180;
    points.push(
      `${center.x + HEX_SIZE * Math.cos(angle)},${center.y + HEX_SIZE * Math.sin(angle)}`,
    );
  }

  return points.join(" ");
}

export function sharedEdgeSegment(
  left: Coord,
  right: Coord,
): Readonly<{ x1: number; y1: number; x2: number; y2: number }> {
  const leftCenter = hexCenter(left);
  const rightCenter = hexCenter(right);
  const midpoint = {
    x: (leftCenter.x + rightCenter.x) / 2,
    y: (leftCenter.y + rightCenter.y) / 2,
  };
  const delta = {
    x: rightCenter.x - leftCenter.x,
    y: rightCenter.y - leftCenter.y,
  };
  const length = Math.hypot(delta.x, delta.y);
  const halfEdge = HEX_SIZE * 0.47;
  const perpendicular = {
    x: (-delta.y / length) * halfEdge,
    y: (delta.x / length) * halfEdge,
  };

  return {
    x1: midpoint.x - perpendicular.x,
    y1: midpoint.y - perpendicular.y,
    x2: midpoint.x + perpendicular.x,
    y2: midpoint.y + perpendicular.y,
  };
}

export function siteViewBox(
  coords: readonly Coord[],
): Readonly<{ x: number; y: number; width: number; height: number }> {
  const centers = coords.map(hexCenter);
  const xValues = centers.map((center) => center.x);
  const yValues = centers.map((center) => center.y);
  const padding = HEX_SIZE * 1.45;
  const minX = Math.min(...xValues) - padding;
  const maxX = Math.max(...xValues) + padding;
  const minY = Math.min(...yValues) - padding;
  const maxY = Math.max(...yValues) + padding;

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}
